// src/pages/business/public/PublicProfile.tsx
// Web adaptation of mobile PublicBusinessProfileScreen - Customer-facing business profile

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  MapPin, Clock, Phone, Globe, Star, Heart, Share2,
  MessageCircle, ChevronRight, Package, Scissors, Loader2,
  Calendar, Award, Users, CheckCircle, ShoppingCart, Plus
} from 'lucide-react';
import apiClient from '@/services/apiClient';
import { useToast } from '@/contexts/ToastContext';
import { getImageUrl } from '@/config/media';

interface BusinessProfile {
  id: string;
  name: string;
  description: string;
  logo_url?: string;
  cover_image_url?: string;
  category: string;
  location: string;
  phone: string;
  website?: string;
  rating: number;
  review_count: number;
  is_verified: boolean;
  opening_hours?: {
    day: string;
    open: string;
    close: string;
    is_open: boolean;
  }[];
  is_open_now: boolean;
  follower_count: number;
  product_count: number;
  service_count: number;
}

interface Product {
  id: string;
  name: string;
  price: number;
  image_url?: string;
  in_stock: boolean;
}

interface Service {
  id: string;
  name: string;
  price: number;
  duration?: number;
  image_url?: string;
}

interface Post {
  id: string;
  content: string;
  media_urls?: string[];
  likes_count: number;
  comments_count: number;
  created_at: string;
}

const PublicProfile: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  const [business, setBusiness] = useState<BusinessProfile | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [posts, setPosts] = useState<Post[]>([]);
  const [activeTab, setActiveTab] = useState<'products' | 'services' | 'posts'>('products');
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState(false);

  useEffect(() => {
    fetchBusinessData();
  }, [businessId]);

  const fetchBusinessData = async () => {
    try {
      setLoading(true);
      const [businessRes, productsRes, servicesRes, postsRes] = await Promise.all([
        apiClient.get(`/businesses/${businessId}/public-profile`),
        apiClient.get(`/businesses/${businessId}/products/public`),
        apiClient.get(`/businesses/${businessId}/services/public`),
        apiClient.get(`/businesses/${businessId}/posts/public`)
      ]);
      setBusiness(businessRes.data);
      setProducts(productsRes.data.products || []);
      setServices(servicesRes.data.services || []);
      setPosts(postsRes.data.posts || []);
      setFollowing(businessRes.data.is_following || false);
    } catch (error: any) {
      console.error('Error fetching business data:', error);
      toastError('Failed to load business profile');
    } finally {
      setLoading(false);
    }
  };

  const toggleFollow = async () => {
    try {
      await apiClient.post(`/businesses/${businessId}/follow`);
      setFollowing(!following);
      if (business) {
        setBusiness({
          ...business,
          follower_count: following ? business.follower_count - 1 : business.follower_count + 1
        });
      }
      toastSuccess(following ? 'Unfollowed business' : 'Following business');
    } catch (error: any) {
      toastError('Failed to update follow status');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: business?.name,
          text: business?.description,
          url: window.location.href
        });
      } catch (error) {
        console.error('Share failed:', error);
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      toastSuccess('Link copied to clipboard');
    }
  };

  const addToCart = async (product: Product) => {
    try {
      await apiClient.post('/cart', {
        product_id: product.id,
        quantity: 1
      });
      toastSuccess('Added to cart');
    } catch (error: any) {
      toastError('Failed to add to cart');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg">
        <div className="flex items-center justify-center py-24">
          <Loader2 size={40} className="animate-spin mx-auto text-primary" />
        </div>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-light-bg dark:bg-dark-bg flex items-center justify-center">
        <div className="text-center">
          <p className="text-light-text dark:text-dark-text font-semibold">Business not found</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 px-6 py-2 rounded-xl text-white font-semibold"
            style={{ backgroundColor: '#4F46E5' }}
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-light-bg dark:bg-dark-bg pb-24">
      {/* Cover Image */}
      <div className="relative h-48 md:h-64 bg-gradient-to-br from-primary/20 to-primary/5">
        {business.cover_image_url ? (
          <img
            src={getImageUrl(business.cover_image_url)}
            alt={business.name}
            className="w-full h-full object-cover"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <div className="text-center">
              <Package size={48} className="mx-auto mb-2 text-primary/30" />
              <p className="text-primary/50 font-medium">{business.category}</p>
            </div>
          </div>
        )}
        <div className="absolute top-4 right-4 flex items-center gap-2">
          <button
            onClick={handleShare}
            className="w-10 h-10 rounded-full bg-light-card/80 dark:bg-dark-card/80 backdrop-blur flex items-center justify-center"
          >
            <Share2 size={18} className="text-light-text dark:text-dark-text" />
          </button>
        </div>
      </div>

      {/* Business Info */}
      <div className="max-w-5xl mx-auto px-4 -mt-16 relative">
        <div className="flex items-end gap-4 mb-4">
          {/* Logo */}
          <div className="w-32 h-32 rounded-2xl bg-light-card dark:bg-dark-card shadow-lg overflow-hidden border-4 border-light-bg dark:border-dark-bg">
            {business.logo_url ? (
              <img
                src={getImageUrl(business.logo_url)}
                alt={business.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-primary/10">
                <span className="text-3xl font-bold text-primary">
                  {business.name.charAt(0).toUpperCase()}
                </span>
              </div>
            )}
          </div>

          {/* Name & Actions */}
          <div className="flex-1 pb-2">
            <div className="flex items-center gap-2 mb-1">
              <h1 className="font-bold text-xl text-light-text dark:text-dark-text">
                {business.name}
              </h1>
              {business.is_verified && (
                <CheckCircle size={18} className="text-blue-500" />
              )}
            </div>
            <p className="text-sm text-light-subtle dark:text-dark-subtle">{business.category}</p>
            <div className="flex items-center gap-1 mt-1">
              <Star size={14} className="text-yellow-500 fill-yellow-500" />
              <span className="font-semibold text-sm text-light-text dark:text-dark-text">
                {business.rating.toFixed(1)}
              </span>
              <span className="text-xs text-light-subtle dark:text-dark-subtle">
                ({business.review_count} reviews)
              </span>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={toggleFollow}
            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold transition-all ${
              following
                ? 'bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-light-text dark:text-dark-text'
                : 'text-white'
            }`}
            style={!following ? { backgroundColor: '#4F46E5' } : {}}
          >
            <Heart size={16} fill={following ? 'currentColor' : 'none'} />
            {following ? 'Following' : 'Follow'}
          </button>
          <button
            onClick={() => {/* Open chat */}}
            className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl font-semibold bg-light-card dark:bg-dark-card border border-light-border dark:border-dark-border text-light-text dark:text-dark-text"
          >
            <MessageCircle size={16} />
            Message
          </button>
        </div>

        {/* Business Details */}
        <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 shadow-sm border border-light-border dark:border-dark-border mb-6">
          <div className="space-y-3">
            {business.location && (
              <div className="flex items-center gap-3">
                <MapPin size={18} className="text-light-subtle dark:text-dark-subtle" />
                <span className="text-sm text-light-text dark:text-dark-text">{business.location}</span>
              </div>
            )}
            {business.phone && (
              <div className="flex items-center gap-3">
                <Phone size={18} className="text-light-subtle dark:text-dark-subtle" />
                <span className="text-sm text-light-text dark:text-dark-text">{business.phone}</span>
              </div>
            )}
            {business.website && (
              <div className="flex items-center gap-3">
                <Globe size={18} className="text-light-subtle dark:text-dark-subtle" />
                <a href={business.website} target="_blank" rel="noopener noreferrer" className="text-sm text-primary hover:underline">
                  {business.website}
                </a>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Clock size={18} className="text-light-subtle dark:text-dark-subtle" />
              <span className={`text-sm ${business.is_open_now ? 'text-green-500 font-medium' : 'text-red-500 font-medium'}`}>
                {business.is_open_now ? 'Open Now' : 'Closed'}
              </span>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 text-center shadow-sm border border-light-border dark:border-dark-border">
            <p className="text-2xl font-bold text-light-text dark:text-dark-text">{business.follower_count}</p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Followers</p>
          </div>
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 text-center shadow-sm border border-light-border dark:border-dark-border">
            <p className="text-2xl font-bold text-light-text dark:text-dark-text">{business.product_count}</p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Products</p>
          </div>
          <div className="bg-light-card dark:bg-dark-card rounded-xl p-4 text-center shadow-sm border border-light-border dark:border-dark-border">
            <p className="text-2xl font-bold text-light-text dark:text-dark-text">{business.service_count}</p>
            <p className="text-xs text-light-subtle dark:text-dark-subtle">Services</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-4 border-b border-light-border dark:border-dark-border">
          <button
            onClick={() => setActiveTab('products')}
            className={`px-4 py-3 font-semibold text-sm transition-colors relative ${
              activeTab === 'products'
                ? 'text-primary'
                : 'text-light-subtle dark:text-dark-subtle hover:text-light-text dark:hover:text-dark-text'
            }`}
          >
            Products
            {activeTab === 'products' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('services')}
            className={`px-4 py-3 font-semibold text-sm transition-colors relative ${
              activeTab === 'services'
                ? 'text-primary'
                : 'text-light-subtle dark:text-dark-subtle hover:text-light-text dark:hover:text-dark-text'
            }`}
          >
            Services
            {activeTab === 'services' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>
          <button
            onClick={() => setActiveTab('posts')}
            className={`px-4 py-3 font-semibold text-sm transition-colors relative ${
              activeTab === 'posts'
                ? 'text-primary'
                : 'text-light-subtle dark:text-dark-subtle hover:text-light-text dark:hover:text-dark-text'
            }`}
          >
            Posts
            {activeTab === 'posts' && (
              <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-primary rounded-full" />
            )}
          </button>
        </div>

        {/* Tab Content */}
        <div className="pb-8">
          {/* Products Tab */}
          {activeTab === 'products' && (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {products.map((product) => (
                <div
                  key={product.id}
                  className="bg-light-card dark:bg-dark-card rounded-xl overflow-hidden shadow-sm border border-light-border dark:border-dark-border group"
                >
                  <div className="aspect-square bg-light-bg dark:bg-dark-bg">
                    {product.image_url ? (
                      <img
                        src={getImageUrl(product.image_url)}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package size={32} className="text-light-subtle dark:text-dark-subtle" />
                      </div>
                    )}
                  </div>
                  <div className="p-3">
                    <p className="font-medium text-sm text-light-text dark:text-dark-text truncate">
                      {product.name}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="font-bold text-primary">
                        KES {product.price.toLocaleString()}
                      </p>
                      <button
                        onClick={() => addToCart(product)}
                        disabled={!product.in_stock}
                        className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors disabled:opacity-50"
                      >
                        <Plus size={14} className="text-primary" />
                      </button>
                    </div>
                    {!product.in_stock && (
                      <p className="text-xs text-red-500 mt-1">Out of stock</p>
                    )}
                  </div>
                </div>
              ))}
              {products.length === 0 && (
                <div className="col-span-full text-center py-12">
                  <Package size={48} className="mx-auto mb-3 text-light-subtle dark:text-dark-subtle" />
                  <p className="text-light-subtle dark:text-dark-subtle">No products available</p>
                </div>
              )}
            </div>
          )}

          {/* Services Tab */}
          {activeTab === 'services' && (
            <div className="space-y-4">
              {services.map((service) => (
                <div
                  key={service.id}
                  className="bg-light-card dark:bg-dark-card rounded-xl p-4 shadow-sm border border-light-border dark:border-dark-border flex items-center gap-4"
                >
                  <div className="w-20 h-20 rounded-lg bg-light-bg dark:bg-dark-bg flex-shrink-0 overflow-hidden">
                    {service.image_url ? (
                      <img
                        src={getImageUrl(service.image_url)}
                        alt={service.name}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Scissors size={24} className="text-light-subtle dark:text-dark-subtle" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-light-text dark:text-dark-text">{service.name}</p>
                    {service.duration && (
                      <p className="text-xs text-light-subtle dark:text-dark-subtle">
                        {service.duration} minutes
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-primary">
                      KES {service.price.toLocaleString()}
                    </p>
                    <button className="text-xs text-primary font-medium mt-1">
                      Book Now
                    </button>
                  </div>
                </div>
              ))}
              {services.length === 0 && (
                <div className="text-center py-12">
                  <Scissors size={48} className="mx-auto mb-3 text-light-subtle dark:text-dark-subtle" />
                  <p className="text-light-subtle dark:text-dark-subtle">No services available</p>
                </div>
              )}
            </div>
          )}

          {/* Posts Tab */}
          {activeTab === 'posts' && (
            <div className="space-y-4">
              {posts.map((post) => (
                <div
                  key={post.id}
                  className="bg-light-card dark:bg-dark-card rounded-xl p-4 shadow-sm border border-light-border dark:border-dark-border"
                >
                  <p className="text-sm text-light-text dark:text-dark-text mb-3">
                    {post.content}
                  </p>
                  {post.media_urls && post.media_urls.length > 0 && (
                    <img
                      src={getImageUrl(post.media_urls[0])}
                      alt="Post"
                      className="w-full h-48 object-cover rounded-lg mb-3"
                    />
                  )}
                  <div className="flex items-center gap-4 text-light-subtle dark:text-dark-subtle">
                    <div className="flex items-center gap-1">
                      <Heart size={14} />
                      <span className="text-xs">{post.likes_count}</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MessageCircle size={14} />
                      <span className="text-xs">{post.comments_count}</span>
                    </div>
                    <span className="text-xs">
                      {new Date(post.created_at).toLocaleDateString('en-GB', {
                        day: '2-digit',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </span>
                  </div>
                </div>
              ))}
              {posts.length === 0 && (
                <div className="text-center py-12">
                  <MessageCircle size={48} className="mx-auto mb-3 text-light-subtle dark:text-dark-subtle" />
                  <p className="text-light-subtle dark:text-dark-subtle">No posts yet</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicProfile;